import { Tag, Tooltip, Button } from 'antd';
import { SettingOutlined, ThunderboltOutlined } from '@ant-design/icons';
import type { QuickFilter } from './quickFilters';
import { spacing } from '../theme/tokens';

export interface QuickFilterBarProps {
  quickFilters: QuickFilter[];
  activeIds: string[];
  onToggle: (id: string) => void;
  /** admin может открыть управление списком быстрых фильтров. */
  canManage?: boolean;
  onManage?: () => void;
}

/**
 * Ряд чипов-переключателей «быстрые фильтры» над гридом (по аналогии со
 * Structure). Клик по чипу включает/выключает его; активные комбинируются по AND.
 * Не рендерится, если фильтров нет и пользователь не может ими управлять.
 */
export function QuickFilterBar({
  quickFilters,
  activeIds,
  onToggle,
  canManage,
  onManage,
}: QuickFilterBarProps): JSX.Element | null {
  if (quickFilters.length === 0 && !canManage) return null;
  const activeSet = new Set(activeIds);

  return (
    <div
      style={{ display: 'flex', gap: spacing.xs, alignItems: 'center', flexWrap: 'wrap' }}
      data-testid="quick-filter-bar"
    >
      <span className="lens-sort-label">
        <ThunderboltOutlined style={{ fontSize: 12 }} />
        Быстрые фильтры
      </span>
      {quickFilters.map((qf) => {
        const active = activeSet.has(qf.id);
        return (
          <Tag.CheckableTag
            key={qf.id}
            checked={active}
            onChange={() => onToggle(qf.id)}
            className="lens-quickfilter-chip"
            data-testid={`quick-filter-${qf.id}`}
            style={active && qf.color ? { background: qf.color, borderColor: qf.color } : undefined}
          >
            {qf.name}
          </Tag.CheckableTag>
        );
      })}
      {quickFilters.length === 0 && (
        <span style={{ color: 'var(--lens-text-muted, #999)', fontSize: 12 }}>пока нет</span>
      )}
      {canManage && (
        <Tooltip title="Настроить быстрые фильтры">
          <Button
            size="small"
            type="text"
            icon={<SettingOutlined />}
            onClick={onManage}
            aria-label="Настроить быстрые фильтры"
            data-testid="quick-filter-manage"
          />
        </Tooltip>
      )}
    </div>
  );
}
